import clsx from 'clsx';
import { useEffect, useState } from 'react';
import CarouselContainer from 'components/Shared/Carousel/CarouselContainer';
import Icon from 'components/ui/Icon';
import Image from 'components/ui/Image';
import { formatNumber } from 'utils/number';
import { useTranslation } from 'hooks/useTranslation';
import { RecordType } from 'types/BeforeAfterPhotos';
import Cta from 'components/ui/ContentBlocks/Cta';
import { default as UpperCarouselContainer } from './Carousel';
import type { ClinicPage } from 'types/Clinic';

type Props = {
  clinic: ClinicPage;
  reviewStats: Record<string, number>;
  customFieldOverrides?: Record<string, unknown>;
  funnelId: string;
};

const Hero = ({ clinic, reviewStats }: Props): JSX.Element => {
  const { locale, t } = useTranslation();
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [startSlide, setStartSlide] = useState(0);
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const imagesCount = clinic.photos.items.length;

  useEffect(() => {
    setTimeout(() => {
      setIsPageLoaded(true);
    }, 500);
  }, []);

  const openGallery = (): void => {
    setIsGalleryOpen(true);
  };

  const getTwImageContainerWidth = (): string => {
    if (imagesCount >= 5) {
      return 'w-2/4';
    }

    if (imagesCount >= 3) {
      return 'w-3/4';
    }

    return 'w-full';
  };

  const photoItems = clinic.photos?.items || [];

  return (
    <>
      {photoItems[0] && (
        <div className="bg-navy lg:min-h-108 pt-22 pb-3 lg:pb-7">
          <div className="max-w-screen-xl mx-auto lg:px-4 relative h-64 lg:h-auto">
            <div className="hidden lg:block">
              <div className="bg-white overflow-hidden relative lg:h-108">
                <div
                  className={clsx(
                    'absolute h-full full-lazyload',
                    isPageLoaded ? 'bg-black' : 'bg-bluishGreyDarker',
                    getTwImageContainerWidth(),
                  )}
                  onClick={() => {
                    setStartSlide(0);
                    setIsGalleryOpen(true);
                  }}
                >
                  <Image
                    className="w-full h-full object-cover object-center transition duration-300 ease-in-out hover:opacity-75 cursor-pointer"
                    src={photoItems[0].url}
                    alt={photoItems[0].description || photoItems[0].title || ''}
                    fill
                    contentful
                    priority
                  />
                </div>
                <div
                  className={clsx(
                    'hidden absolute lg:pl-2 w-1/4',
                    imagesCount >= 3 &&
                      'lg:flex lg:flex-col h-full lg:min-h-108 space-y-2',
                  )}
                  style={{ left: imagesCount <= 4 ? '75%' : '50%' }}
                >
                  <div className="h-full lg:min-h-108">
                    {photoItems[1] && (
                      <div
                        className="overflow-hidden full-lazyload pb-1 "
                        style={{ height: '50%' }}
                      >
                        <div
                          className={clsx(
                            'relative h-full',
                            isPageLoaded ? 'bg-black' : 'bg-bluishGreyDarker',
                          )}
                          onClick={() => {
                            setStartSlide(1);
                            setIsGalleryOpen(true);
                          }}
                        >
                          <Image
                            src={photoItems[1].url}
                            className="w-full object-cover h-full transition duration-300 ease-in-out hover:opacity-75 cursor-pointer"
                            alt={
                              photoItems[1].description ||
                              photoItems[1].title ||
                              ''
                            }
                            fill
                            contentful
                          />
                        </div>

                        <div className="bg-black" />
                      </div>
                    )}
                    {photoItems[2] && (
                      <div
                        className="overflow-hidden full-lazyload pt-1"
                        style={{ height: '50%' }}
                      >
                        <div
                          className={clsx(
                            'relative h-full',
                            isPageLoaded ? 'bg-black' : 'bg-bluishGreyDarker',
                          )}
                          onClick={() => {
                            setStartSlide(2);
                            setIsGalleryOpen(true);
                          }}
                        >
                          <Image
                            src={photoItems[2].url}
                            className="w-full object-cover h-full transition duration-300 ease-in-out hover:opacity-75 cursor-pointer"
                            alt={
                              photoItems[2].description ||
                              photoItems[2].title ||
                              ''
                            }
                            fill
                            contentful
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div
                  className={clsx(
                    'hidden absolute lg:pl-2 right-0 w-1/4',
                    imagesCount > 4 &&
                      'lg:flex lg:flex-col h-full lg:min-h-108 space-y-2',
                  )}
                >
                  <div className="h-full lg:min-h-108">
                    {photoItems[3] && (
                      <div
                        className="h-full overflow-hidden full-lazyload pb-1"
                        style={{ height: '50%' }}
                      >
                        <div
                          className={clsx(
                            'relative h-full',
                            isPageLoaded ? 'bg-black' : 'bg-bluishGreyDarker',
                          )}
                          onClick={() => {
                            setStartSlide(3);
                            setIsGalleryOpen(true);
                          }}
                        >
                          <Image
                            src={photoItems[3].url}
                            className="w-full object-cover h-full transition duration-300 ease-in-out hover:opacity-75 cursor-pointer"
                            alt={
                              photoItems[3].description ||
                              photoItems[3].title ||
                              ''
                            }
                            fill
                            contentful
                          />
                        </div>
                      </div>
                    )}
                    {photoItems[4] && (
                      <div
                        className="h-full overflow-hidden full-lazyload pt-1"
                        style={{ height: '50%' }}
                      >
                        <div
                          className={clsx(
                            'relative h-full',
                            isPageLoaded ? 'bg-black' : 'bg-bluishGreyDarker',
                          )}
                          onClick={() => {
                            setStartSlide(4);
                            setIsGalleryOpen(true);
                          }}
                        >
                          <Image
                            src={photoItems[4].url}
                            className="w-full object-cover h-full transition duration-300 ease-in-out hover:opacity-75 cursor-pointer"
                            alt={
                              photoItems[4].description ||
                              photoItems[4].title ||
                              ''
                            }
                            fill
                            contentful
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {(imagesCount > 5 || imagesCount === 2 || imagesCount === 4) && (
                <button
                  className="rounded font-netto px-5 leading-9 bg-white absolute right-0 lg:-mt-12 mr-8 hidden lg:block"
                  style={{ boxShadow: '0 0 4px rgba(0, 0, 0, 0.25)' }}
                  onClick={() => {
                    setIsGalleryOpen(true);
                  }}
                >
                  {t('showAllPhotos')}
                </button>
              )}
            </div>
            <UpperCarouselContainer
              className="lg:hidden"
              slides={photoItems}
              onImageClick={openGallery}
            />
          </div>
        </div>
      )}
      <div className="bg-navy pb-12 lg:pb-16">
        <div className="max-w-screen-xl mx-auto px-5 md:px-4">
          <div className="lg:grid grid-cols-2 lg:items-center lg:justify-between">
            <div className="col-span-1 flex flex-col gap-2">
              {clinic.verifiedDate && (
                <p className="font-bold text-white lg:text-sm flex items-center lg:mb-4">
                  <Icon type="VerifiedClinic" className="mr-2" />
                  {t('verifiedClinic')}
                </p>
              )}
              {clinic.name && (
                <h1
                  className="doctor-clinic-name text-white mb-1 py-0 lg:mb-3"
                  dangerouslySetInnerHTML={{ __html: clinic.name }}
                />
              )}
              {clinic.city.name && clinic.city.country.name && (
                <p className="text-sandBlueLighter flex items-center">
                  <Icon
                    type="LocationPinAlternate"
                    color="#7397A1"
                    className="mt-1"
                  />
                  <span className="text-base ml-3 lg:ml-4">
                    {clinic.city.name}, {clinic.city.country.name}
                  </span>
                </p>
              )}
              {reviewStats && reviewStats.avg_rating && (
                <div className="flex items-center text-white">
                  <p className="text-lg flex items-center">
                    <Icon type="Star" className="mr-2" color="#7397A1" />
                    <span className="font-bold ml-1 mr-2 lg:ml-2">
                      {formatNumber(
                        reviewStats.avg_rating.toFixed(1) || '',
                        locale,
                      )}
                    </span>{' '}
                    ({formatNumber(reviewStats.total, locale)}{' '}
                    {t('doctorProfile.reviews')})
                  </p>
                </div>
              )}
              {clinic.translatedLanguages && (
                <div className="flex items-center text-white text-lg">
                  <Icon
                    type="Languages"
                    color="#7397A1"
                    className="mr-2 shrink-0"
                  />
                  <p className="flex flex-wrap">
                    {clinic.translatedLanguages?.map(
                      (language: string, idx: number) => (
                        <span key={idx}>
                          {t(`language.${language}`)}
                          {clinic.translatedLanguages &&
                          idx !== clinic.translatedLanguages.length - 1 ? (
                            <>,&nbsp;</>
                          ) : null}
                        </span>
                      ),
                    )}
                  </p>
                </div>
              )}
            </div>
            {clinic.cta && (
              <div className="mt-3 lg:mt-0">
                <Cta {...clinic.cta} />
              </div>
            )}
          </div>
        </div>
      </div>
      {isGalleryOpen && (
        <CarouselContainer
          slides={photoItems}
          onClose={() => {
            setIsGalleryOpen(false);
          }}
          startSlide={startSlide}
          pageType={RecordType.DOCTOR}
          slug={clinic.slug as string}
        />
      )}
    </>
  );
};

export default Hero;
